import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SelectField } from "./Form";
import { Button, ConfirmDialog, Pill, Section, formatRelative, useAuth, useToast, type PillTone } from "@ozpanel/ui";

// MailSyncPanel — import or sync another mail account into a mailbox here (mail_sync.go).
//
// ONE component for both audiences: a tenant or operator (any mailbox in scope, with a destination
// picker) and a mailbox's own user (their mailbox). The API is the same shape under both paths; only
// which rows a caller may see differs, and the server decides that. The calls are written out as
// call sites per audience (syncApi) rather than built from a prop, so every call is visible to the
// console reachability checks.

type Audience = "tenant" | "me";
type Client = ReturnType<typeof useAuth>["client"];

// Every call written out for both audiences — explicit literal call sites, so the console
// reachability checks see each route as reached (a path assembled from a prop is invisible to them).
function syncApi(client: Client, audience: Audience) {
  const me = audience === "me";
  type List = { items: MailSyncRow[] | null; engine_here?: string };
  return {
    list: () => (me ? client.apiFetch<List>("/mail/me/migrations") : client.apiFetch<List>("/mail/migrations")),
    create: (body: string) =>
      me
        ? client.apiFetch("/mail/me/migrations", { method: "POST", body })
        : client.apiFetch("/mail/migrations", { method: "POST", body }),
    log: (id: number) =>
      me ? client.apiFetch<{ log: string }>(`/mail/me/migrations/${id}`) : client.apiFetch<{ log: string }>(`/mail/migrations/${id}`),
    update: (id: number, body: string) =>
      me
        ? client.apiFetch(`/mail/me/migrations/${id}`, { method: "PATCH", body })
        : client.apiFetch(`/mail/migrations/${id}`, { method: "PATCH", body }),
    action: (id: number, action: string) =>
      me
        ? client.apiFetch(`/mail/me/migrations/${id}/action`, { method: "POST", body: JSON.stringify({ action }) })
        : client.apiFetch(`/mail/migrations/${id}/action`, { method: "POST", body: JSON.stringify({ action }) }),
    remove: (id: number) =>
      me
        ? client.apiFetch(`/mail/me/migrations/${id}`, { method: "DELETE" })
        : client.apiFetch(`/mail/migrations/${id}`, { method: "DELETE" }),
  };
}

export interface MailSyncRow {
  id: number;
  dest: string;
  source_host: string;
  source_port: number;
  source_user: string;
  mode: "once" | "pull" | "two_way";
  interval_minutes: number;
  scan_spam: boolean;
  remote_junk: string;
  enabled: boolean;
  status: string;
  transferred: number;
  pushed: number;
  scanned: number;
  spam_moved: number;
  runs: number;
  failures: number;
  message?: string;
  created_by: string;
  running_on?: string;
  last_node?: string;
  last_run_at?: string;
  next_run_at?: string;
}

const MODE_LABEL: Record<MailSyncRow["mode"], string> = {
  once: "One-time copy",
  pull: "Keep in sync (one-way)",
  two_way: "Two-way sync",
};

const MODE_HELP: Record<MailSyncRow["mode"], string> = {
  once: "Copies all mail once, then stops. Nothing is deleted anywhere.",
  pull: "Keeps copying new mail and read/flag changes from the other account into this one. Nothing is ever deleted — here or there.",
  two_way:
    "Mirrors both accounts: new mail, read/flag changes, moves and deletions made on either side appear on the other. Deleting here deletes there.",
};

const input = "w-full rounded border border-line bg-surface px-2 py-1.5 text-[13px]";

function tone(r: MailSyncRow): PillTone {
  if (r.running_on) return "accent";
  if (r.status === "failed") return "crit";
  if (r.failures > 0) return "warn";
  if (r.status === "completed" || r.status === "idle") return "ok";
  return "neutral";
}

// Node names are the operator's vocabulary; a customer is told that it runs and when, not where.
function stateLabel(r: MailSyncRow, showNodes: boolean): string {
  if (r.running_on) return showNodes ? `running on ${r.running_on}` : "running";
  if (r.status === "idle" && r.next_run_at) return `next ${formatRelative(r.next_run_at)}`;
  if (r.failures > 0 && r.status !== "failed") return `${r.status} · ${r.failures} failed run(s)`;
  return r.status;
}

export interface MailSyncPanelProps {
  /** "tenant" (any mailbox in the caller's scope) or "me" (the signed-in mailbox). */
  audience: Audience;
  /** The operator console names the node a run used; a customer's never does. */
  showNodes?: boolean;
  /** Tenant console only: pick the destination mailbox. Omitted = the caller's own mailbox. */
  destPicker?: (value: { dest_domain: string; dest_localpart: string }, set: (v: { dest_domain: string; dest_localpart: string }) => void) => React.ReactNode;
  title?: string;
}

export function MailSyncPanel({ audience, destPicker, title = "Import & sync other mail accounts", showNodes = false }: MailSyncPanelProps) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const api = syncApi(client, audience);
  const key = ["mail-syncs", audience];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api.list(),
    refetchInterval: 10000,
    retry: false,
  });
  const [adding, setAdding] = React.useState(false);
  const [editing, setEditing] = React.useState<MailSyncRow | null>(null);
  const [logOf, setLogOf] = React.useState<MailSyncRow | null>(null);
  const [removing, setRemoving] = React.useState<{ row: MailSyncRow; kind: "cancel" | "delete" } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: number; action: string }) =>
      api.action(id, action),
    onSuccess: (_r, v) => {
      notify({ kind: "success", title: { run: "Running now", pause: "Paused", resume: "Resumed", cancel: "Cancelled" }[v.action] ?? "Done" });
      refresh();
    },
    onError: (e) => notify({ kind: "error", title: "That did not work", body: e instanceof Error ? e.message : String(e) }),
  });

  const rows = list.data?.items ?? [];
  return (
    <Section
      title={title}
      hint={rows.length ? String(rows.length) : undefined}
      actions={
        !adding && (
          <Button size="sm" variant="accent" onClick={() => setAdding(true)}>
            Add an account
          </Button>
        )
      }
    >
      <div className="space-y-3">
        {adding && <SyncForm audience={audience} destPicker={destPicker} onDone={() => { setAdding(false); refresh(); }} />}
        {list.error && <p className="text-[12.5px] text-crit">{String((list.error as Error).message)}</p>}
        {!list.isLoading && rows.length === 0 && !adding && (
          <p className="text-[12.5px] text-muted">
            No other accounts connected. Add one to copy its mail here once, keep it flowing in, or keep both in sync.
          </p>
        )}
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[13px]">
                  <span className="font-mono">{r.source_user}</span>
                  <span className="text-muted"> @ {r.source_host}</span>
                  {destPicker && (
                    <>
                      <span className="text-muted"> → </span>
                      <span className="font-mono">{r.dest}</span>
                    </>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted">
                  <Pill tone="neutral">{MODE_LABEL[r.mode] ?? r.mode}</Pill>
                  {r.mode !== "once" && <span>every {r.interval_minutes} min</span>}
                  {r.scan_spam && <Pill tone="accent">spam scan on</Pill>}
                  <Pill tone={tone(r)}>{stateLabel(r, showNodes)}</Pill>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.enabled && !r.running_on && (
                  <Button size="sm" variant="soft" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "run" })}>
                    Run now
                  </Button>
                )}
                {r.mode !== "once" && r.enabled && (
                  <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "pause" })}>
                    Pause
                  </Button>
                )}
                {!r.enabled && r.status !== "completed" && r.status !== "cancelled" && (
                  <Button size="sm" variant="soft" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "resume" })}>
                    Resume
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                  Settings
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setLogOf(r)}>
                  Log
                </Button>
                {r.enabled ? (
                  <Button size="sm" variant="danger" onClick={() => setRemoving({ row: r, kind: "cancel" })}>
                    Cancel
                  </Button>
                ) : (
                  <Button size="sm" variant="danger" disabled={!!r.running_on} onClick={() => setRemoving({ row: r, kind: "delete" })}>
                    Delete
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-2 grid gap-x-6 gap-y-1 text-[12px] sm:grid-cols-4">
              <span>
                <span className="text-muted">Copied here </span>
                {r.transferred}
              </span>
              {r.mode === "two_way" && (
                <span>
                  <span className="text-muted">Copied there </span>
                  {r.pushed}
                </span>
              )}
              {r.scan_spam && (
                <span>
                  <span className="text-muted">Scanned </span>
                  {r.scanned}
                  <span className="text-muted"> · to Junk </span>
                  {r.spam_moved}
                </span>
              )}
              <span className="text-muted">
                {r.last_run_at ? `last run ${formatRelative(r.last_run_at)}${showNodes && r.last_node ? ` on ${r.last_node}` : ""}` : "not run yet"}
              </span>
            </div>
            {r.message && <p className={`mt-1 text-[12px] ${r.status === "failed" || r.failures > 0 ? "text-crit" : "text-muted"}`}>{r.message}</p>}
          </div>
        ))}
      </div>

      {editing && <EditSync audience={audience} row={editing} onClose={() => { setEditing(null); refresh(); }} />}
      {logOf && <SyncLog audience={audience} row={logOf} onClose={() => setLogOf(null)} />}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing?.kind === "cancel" ? `Stop syncing ${removing.row.source_user}?` : `Delete this sync?`}
        confirmLabel={removing?.kind === "cancel" ? "Stop it" : "Delete"}
        destructive
        body={
          <p>
            {removing?.kind === "cancel"
              ? "It stops where it is. Mail already copied stays — here and on the other account."
              : "Removes the connection and its saved password. Mail already copied stays in the mailbox."}
          </p>
        }
        onConfirm={async () => {
          const r = removing;
          setRemoving(null);
          if (!r) return;
          try {
            if (r.kind === "cancel") {
              await api.action(r.row.id, "cancel");
            } else {
              await api.remove(r.row.id);
            }
            refresh();
          } catch (e) {
            notify({ kind: "error", title: "That did not work", body: e instanceof Error ? e.message : String(e) });
          }
        }}
      />
    </Section>
  );
}

function ModePicker({ value, onChange }: { value: MailSyncRow["mode"]; onChange: (m: MailSyncRow["mode"]) => void }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      {(Object.keys(MODE_LABEL) as MailSyncRow["mode"][]).map((m) => (
        <label
          key={m}
          className={`cursor-pointer rounded-lg border p-2.5 text-[12.5px] ${value === m ? "border-[var(--ozp-accent-line)] bg-accentsoft" : "border-line"}`}
        >
          <input type="radio" className="mr-2" checked={value === m} onChange={() => onChange(m)} />
          <span className="font-medium">{MODE_LABEL[m]}</span>
          <span className="mt-1 block text-[11.5px] leading-snug text-muted">{MODE_HELP[m]}</span>
        </label>
      ))}
    </div>
  );
}

function ScanToggle({ value, onChange, mode }: { value: boolean; onChange: (v: boolean) => void; mode: MailSyncRow["mode"] }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-[12.5px]">
      <input type="checkbox" className="mt-0.5" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="font-medium">Scan incoming mail for spam</span>
        <span className="block text-[11.5px] leading-snug text-muted">
          Every message that arrives in the Inbox from the other account is checked by the spam filter, and spam is moved to Junk
          {mode === "two_way" ? " — here and, on the next sync, on the other account too." : "."} Uses this mailbox's own spam level.
          It never teaches the filter; only your own moves into and out of Junk do that.
        </span>
      </span>
    </label>
  );
}

function SyncForm({ audience, destPicker, onDone }: { audience: Audience; destPicker?: MailSyncPanelProps["destPicker"]; onDone: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [dest, setDest] = React.useState({ dest_domain: "", dest_localpart: "" });
  const [f, setF] = React.useState({ source_host: "", source_port: "993", source_user: "", source_password: "", interval_minutes: "15", remote_junk: "" });
  const [mode, setMode] = React.useState<MailSyncRow["mode"]>("once");
  const [scan, setScan] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      await syncApi(client, audience).create(
        JSON.stringify({
          ...(destPicker ? dest : {}),
          source_host: f.source_host.trim(),
          source_port: Number(f.source_port) || 993,
          source_user: f.source_user.trim(),
          source_password: f.source_password,
          mode,
          interval_minutes: Number(f.interval_minutes) || 15,
          scan_spam: scan,
          remote_junk: f.remote_junk.trim(),
        }),
      );
      notify({ kind: "success", title: "Connected — the first run starts within a minute" });
      onDone();
    } catch (e) {
      notify({ kind: "error", title: "Could not connect that account", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-line p-3">
      {destPicker && destPicker(dest, setDest)}
      <div className="grid gap-2 sm:grid-cols-4">
        <label className="text-[12px] sm:col-span-2">
          <span className="text-muted">Other account's IMAP server</span>
          <input className={input} placeholder="imap.gmail.com" value={f.source_host} onChange={set("source_host")} />
        </label>
        <label className="text-[12px]">
          <span className="text-muted">Port</span>
          <input className={input} inputMode="numeric" value={f.source_port} onChange={set("source_port")} />
        </label>
        <div className="self-end pb-1 text-[11.5px] text-muted">993 = TLS · 143 = STARTTLS. Never unencrypted.</div>
        <label className="text-[12px] sm:col-span-2">
          <span className="text-muted">Login</span>
          <input className={input} autoComplete="off" value={f.source_user} onChange={set("source_user")} />
        </label>
        <label className="text-[12px] sm:col-span-2">
          <span className="text-muted">Password (Gmail/Outlook: an app password)</span>
          <input className={input} type="password" autoComplete="new-password" value={f.source_password} onChange={set("source_password")} />
        </label>
      </div>
      <ModePicker value={mode} onChange={setMode} />
      {mode !== "once" && (
        <label className="block text-[12px]">
          <span className="text-muted">Check every (minutes, 5–1440)</span>
          <input className={`${input} max-w-[8rem]`} inputMode="numeric" value={f.interval_minutes} onChange={set("interval_minutes")} />
        </label>
      )}
      <ScanToggle value={scan} onChange={setScan} mode={mode} />
      <label className="block text-[12px]">
        <span className="text-muted">The other account's spam folder (leave empty to detect it)</span>
        <input className={`${input} max-w-sm`} placeholder="detected: Junk, Spam, [Gmail]/Spam…" value={f.remote_junk} onChange={set("remote_junk")} />
      </label>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="accent"
          disabled={busy || !f.source_host || !f.source_user || !f.source_password || (!!destPicker && (!dest.dest_domain || !dest.dest_localpart))}
          onClick={submit}
        >
          Connect
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl border border-line bg-surface p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 text-[14px] font-medium">{title}</div>
        {children}
      </div>
    </div>
  );
}

function EditSync({ audience, row, onClose }: { audience: Audience; row: MailSyncRow; onClose: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [mode, setMode] = React.useState(row.mode);
  const [scan, setScan] = React.useState(row.scan_spam);
  const [interval, setInterval] = React.useState(String(row.interval_minutes));
  const [junk, setJunk] = React.useState(row.remote_junk);
  const [pass, setPass] = React.useState("");
  const save = async () => {
    try {
      await syncApi(client, audience).update(
        row.id,
        JSON.stringify({
          mode,
          scan_spam: scan,
          interval_minutes: Number(interval) || row.interval_minutes,
          remote_junk: junk.trim(),
          ...(pass ? { source_password: pass } : {}),
        }),
      );
      notify({ kind: "success", title: "Saved — applies from the next run" });
      onClose();
    } catch (e) {
      notify({ kind: "error", title: "Could not save", body: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <Modal title={`${row.source_user} @ ${row.source_host}`} onClose={onClose}>
      <div className="space-y-3">
        <ModePicker value={mode} onChange={setMode} />
        {mode !== "once" && (
          <label className="block text-[12px]">
            <span className="text-muted">Check every (minutes)</span>
            <input className={`${input} max-w-[8rem]`} value={interval} onChange={(e) => setInterval(e.target.value)} />
          </label>
        )}
        <ScanToggle value={scan} onChange={setScan} mode={mode} />
        <label className="block text-[12px]">
          <span className="text-muted">The other account's spam folder</span>
          <input className={`${input} max-w-sm`} value={junk} placeholder="detect" onChange={(e) => setJunk(e.target.value)} />
        </label>
        <label className="block text-[12px]">
          <span className="text-muted">New password for the other account (only if it changed)</span>
          <input className={`${input} max-w-sm`} type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} />
        </label>
        <div className="flex gap-2">
          <Button size="sm" variant="accent" onClick={save}>
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function SyncLog({ audience, row, onClose }: { audience: Audience; row: MailSyncRow; onClose: () => void }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["mail-sync-log", audience, row.id],
    queryFn: () => syncApi(client, audience).log(row.id),
    refetchInterval: 5000,
  });
  return (
    <Modal title={`Last run — ${row.source_user} @ ${row.source_host}`} onClose={onClose}>
      <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-line bg-bg2 px-3 py-2 font-mono text-[11.5px]">
        {q.data?.log || (q.isLoading ? "Loading…" : "No run has written a log yet.")}
      </pre>
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

/** The destination of an import / sync: a domain and a mailbox that must ALREADY exist (mail is
 * copied into it) — pickers of what exists rather than free-text boxes a typo silently invalidates.
 * The server still refuses a mailbox that does not exist rather than creating one. */
export function MailboxDestPicker({
  value,
  set,
}: {
  value: { dest_domain: string; dest_localpart: string };
  set: (v: { dest_domain: string; dest_localpart: string }) => void;
}) {
  const { client } = useAuth();
  const domains = useQuery({
    queryKey: ["import-domains"],
    queryFn: () => client.apiFetch<{ items: { domain?: string; fqdn?: string }[] | null }>("/mail/domains"),
    retry: false,
  });
  const domainOpts = (domains.data?.items ?? []).map((d) => String(d.domain ?? d.fqdn ?? "")).filter(Boolean);
  const mailboxes = useQuery({
    queryKey: ["import-mailboxes", value.dest_domain],
    enabled: !!value.dest_domain,
    queryFn: () => client.apiFetch<{ items: { localpart?: string }[] | null }>(`/mail/domains/${encodeURIComponent(value.dest_domain)}/mailboxes`),
    retry: false,
  });
  const mboxOpts = (mailboxes.data?.items ?? []).map((m) => String(m.localpart ?? "")).filter(Boolean);
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <SelectField
        label="Into which domain"
        value={value.dest_domain}
        onChange={(v) => set({ dest_domain: v, dest_localpart: "" })}
        options={[
          { value: "", label: domains.isLoading ? "Loading…" : "Choose a domain" },
          ...domainOpts.map((d) => ({ value: d, label: d })),
        ]}
      />
      <SelectField
        label="Into which mailbox"
        value={value.dest_localpart}
        onChange={(v) => set({ ...value, dest_localpart: v })}
        disabled={!value.dest_domain}
        options={[
          {
            value: "",
            label: !value.dest_domain
              ? "Choose a domain first"
              : mailboxes.isLoading
                ? "Loading…"
                : mboxOpts.length
                  ? "Choose a mailbox"
                  : "No mailboxes here — create one first",
          },
          ...mboxOpts.map((lp) => ({ value: lp, label: `${lp}@${value.dest_domain}` })),
        ]}
      />
    </div>
  );
}

